
import React, { createContext, useContext, useState, useEffect } from 'react';
import { detectConnectionSpeed, ConnectionSpeed } from '@/utils/networkSpeedDetector';

interface AdaptiveLoadingContextType {
  connectionSpeed: ConnectionSpeed;
  isSlowConnection: boolean;
  isVerySlowConnection: boolean;
  useLowDataMode: boolean;
  setUseLowDataMode: (value: boolean) => void;
  deferNonCriticalItems: boolean;
}

const AdaptiveLoadingContext = createContext<AdaptiveLoadingContextType>({
  connectionSpeed: 'fast',
  isSlowConnection: false,
  isVerySlowConnection: false,
  useLowDataMode: false,
  setUseLowDataMode: () => {},
  deferNonCriticalItems: false
});

export function useAdaptiveLoading() {
  return useContext(AdaptiveLoadingContext);
}

interface AdaptiveLoadingProviderProps {
  children: React.ReactNode;
}

export function AdaptiveLoadingProvider({ children }: AdaptiveLoadingProviderProps) {
  const [connectionSpeed, setConnectionSpeed] = useState<ConnectionSpeed>('fast');
  const [useLowDataMode, setUseLowDataMode] = useState(false);
  
  // Determine if we have a slow connection
  const isVerySlowConnection = connectionSpeed === 'very-slow';
  const isSlowConnection = connectionSpeed === 'slow' || isVerySlowConnection;
  const deferNonCriticalItems = isSlowConnection || useLowDataMode;
  
  // Monitor connection speed
  useEffect(() => {
    const initialSpeed = detectConnectionSpeed();
    setConnectionSpeed(initialSpeed);
    
    // If we have a very slow connection, default to low data mode
    if (initialSpeed === 'very-slow') {
      setUseLowDataMode(true);
    }
    
    // Set up connection monitoring
    const handleConnectionChange = () => {
      const newSpeed = detectConnectionSpeed();
      if (newSpeed !== connectionSpeed) {
        setConnectionSpeed(newSpeed);
        
        // Auto-enable low data mode on very slow connections
        if (newSpeed === 'very-slow') {
          setUseLowDataMode(true);
        }
      }
    };
    
    // Listen for connection changes
    window.addEventListener('online', handleConnectionChange);
    window.addEventListener('offline', handleConnectionChange);
    
    if ('connection' in navigator) {
      // @ts-ignore - Connection API may not be fully typed
      navigator.connection?.addEventListener('change', handleConnectionChange);
    }
    
    // Check speed periodically - network conditions can change
    const intervalId = setInterval(handleConnectionChange, 30000);
    
    return () => {
      window.removeEventListener('online', handleConnectionChange);
      window.removeEventListener('offline', handleConnectionChange);
      if ('connection' in navigator) {
        // @ts-ignore - Connection API may not be fully typed
        navigator.connection?.removeEventListener('change', handleConnectionChange);
      }
      clearInterval(intervalId);
    };
  }, [connectionSpeed]);
  
  const value = {
    connectionSpeed,
    isSlowConnection,
    isVerySlowConnection,
    useLowDataMode,
    setUseLowDataMode,
    deferNonCriticalItems
  };
  
  return (
    <AdaptiveLoadingContext.Provider value={value}>
      {children}
    </AdaptiveLoadingContext.Provider>
  );
}
